import { useEffect, useState } from 'react';

import {
  TAuth,
  TAuthState,
  IOktaIdToken,
  IOktaAccessToken,
} from '../interfaces';

import getIsCypressOnLocalhost from './getIsCypressOnLocalhost';

type TUseVerifyAuth = {
  authService: TAuth;
  authState: TAuthState;
  handleRedirect: () => void;
  oktaAccessToken: IOktaAccessToken | null;
  oktaClientId: string;
  oktaIdToken: IOktaIdToken | null;
  oktaScopes: string;
  refreshOktaTokens: (authService: TAuth) => Promise<void>;
  saveOktaAccessToken: (accessToken: IOktaAccessToken, auth: TAuth) => void;
  saveOktaIdToken: (idToken: IOktaIdToken, auth: TAuth) => void;
};

export const useVerifyAuth = function ({
  authService,
  authState,
  handleRedirect,
  oktaAccessToken,
  oktaClientId,
  oktaIdToken,
  oktaScopes = '',
  refreshOktaTokens,
  saveOktaAccessToken,
  saveOktaIdToken,
}: TUseVerifyAuth) {
  const _haveTokensLoaded = getIsCypressOnLocalhost()
    ? window.Cypress?.haveTokensLoaded
    : !!(oktaAccessToken && oktaIdToken);

  const [haveTokensLoaded, sethaveTokensLoaded] = useState(
    getIsCypressOnLocalhost() && window.Cypress?.haveTokensLoaded
  );
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  useEffect(
    function checkAuthenticationAndGetTokens() {
      const oktaAuth = authService._oktaAuth;

      function saveTokens(authToken: IOktaAccessToken, idToken: IOktaIdToken) {
        Promise.all([
          saveOktaAccessToken(authToken, authService),
          saveOktaIdToken(idToken, authService),
        ])
          .then(() => {
            sethaveTokensLoaded(true);
            setIsCheckingAuth(false);
          })
          .catch(() => handleRedirect());
      }

      if (!_haveTokensLoaded && !isCheckingAuth) {
        setIsCheckingAuth(true);
        const { isAuthenticated, idToken, accessToken } = authState;
        if (isAuthenticated && idToken && accessToken) {
          const scopes = oktaScopes.split(' ');
          const idTokenClaims = oktaAuth.token.decode(idToken).payload;
          const idTokenPayload = {
            claims: oktaAuth.token.decode(idToken).payload,
            clientId: oktaClientId,
            expiresAt: idTokenClaims.exp,
            idToken,
            scopes,
          };

          const authTokenClaims = oktaAuth.token.decode(accessToken).payload;
          const authTokenPayload = {
            accessToken,
            expiresAt: authTokenClaims.exp,
            scopes,
            tokenType: 'Bearer',
          };

          saveTokens(authTokenPayload, idTokenPayload);
        } else {
          oktaAuth.session
            .get()
            .then((res) => {
              if (res.status === 'ACTIVE') {
                oktaAuth.token
                  .getWithoutPrompt({
                    responseType: ['id_token', 'token'],
                  })
                  .then(({ tokens: { accessToken, idToken } }) =>
                    saveTokens(accessToken, idToken)
                  )
                  .catch(() => handleRedirect());
              } else {
                handleRedirect();
              }
            })
            .catch(() => handleRedirect());
        }
      }
    },
    [
      oktaAccessToken,
      oktaIdToken,
      handleRedirect,
      saveOktaAccessToken,
      saveOktaIdToken,
      _haveTokensLoaded,
      authService,
      authState,
      isCheckingAuth,
    ]
  );

  useEffect(
    function refreshOktaTokensOnLoad() {
      if (_haveTokensLoaded && !getIsCypressOnLocalhost()) {
        refreshOktaTokens(authService).then(() => sethaveTokensLoaded(true));
      }
    },
    [refreshOktaTokens, _haveTokensLoaded, authService]
  );

  return {
    haveTokensLoaded,
  };
};
